// src/services/provider.js

// Base API URL - consider moving to an environment variable
export const API_BASE_URL = `${import.meta.env.VITE_API_URL}/api`;
const API_KEY = import.meta.env.VITE_API_KEY

/**
 * Fetch all providers or search with a query
 * @param {string} query - Optional search query
 * @param {string} language - Optional language filter
 * @returns {Promise<Array>} - Array of provider objects
 */
export async function fetchProviders(query = '', language = '', page = 1, pageSize = 10) {
  let url = `${API_BASE_URL}/providers`;

  // Add query parameters
  const params = new URLSearchParams();
  if (query) params.append('q', query);
  params.append('language', language || 'english');
  params.append('page', page.toString());
  params.append('page_size', pageSize.toString());

  url += `?${params.toString()}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'X-API-Key': API_KEY
    }
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();

  return {
    providers: data.providers || [],
    pagination: {
      total: data.total || 0,
      page: data.page || 1,
      page_size: data.page_size || 10,
      total_pages: data.total_pages || 1
    }
  };
}

/**
 * Fetch providers for validator review (includes raw/agency data)
 * @param {string} query - Optional search query
 * @param {string} language - Optional language filter
 * @param {number} page - Page number
 * @param {number} pageSize - Number of items per page
 * @param {string} source - Optional source filter ('agency' or 'provider')
 * @param {Array<string>} status - Optional status filter array
 * @returns {Promise<Object>} - Object with providers array and pagination info
 */
export async function fetchProvidersForValidator(token, query = '', language = '', page = 1, pageSize = 10, source = null, status = null, city = null, state = null) {
  let url = `${API_BASE_URL}/providers/protected/search/validator`;

  // Add query parameters
  const params = new URLSearchParams();
  if (query) params.append('q', query);
  params.append('language', language || 'english');
  params.append('page', page.toString());
  params.append('per_page', pageSize.toString());
  if (source) params.append('source', source);
  if (status) params.append('status', status);
  if (city) params.append('city', city);
  if (state) params.append('state', state);

  url += `?${params.toString()}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'X-API-Key': API_KEY,
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();

  return {
    providers: data.providers || [],
    pagination: {
      total: data.total || 0,
      page: data.page || 1,
      page_size: data.page_size || 10,
      total_pages: data.total_pages || 1
    }
  };
}

/**
 * Get a single provider by ID
 * @param {string|number} id - Provider ID
 * @returns {Promise<Object>} - Provider object
 */
export async function getProvider(id, language = 'english') {
  const response = await fetch(`${API_BASE_URL}/providers/${id}?language=${encodeURIComponent(language)}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'X-API-Key': API_KEY
    }
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
}

/**
 * Get the current user's provider profile
 * @param {string} token - JWT token for authentication
 * @param {string} language - Language for localization (default: 'english')
 * @returns {Promise<Object>} - Provider profile or status message
 */
export async function fetchProviderProfile(token, language = 'english') {
  const response = await fetch(
    `${API_BASE_URL}/providers/protected/profile?language=${encodeURIComponent(language)}`,
    {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'X-API-Key': API_KEY
      }
    }
  );

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
}

/**
 * Get phone numbers for a provider by ID
 * @param {string|number} id - Provider ID
 * @returns {Promise<string[]>} - Array of phone numbers
 */
export async function getProviderPhoneNumbers(id) {
  const response = await fetch(`${API_BASE_URL}/providers/${id}/phone-numbers`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'X-API-Key': API_KEY
    }
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
}

/**
 * Format provider data from API to form-friendly format
 * @param {Object} providerData - Provider data from API 
 * @returns {Object} - Formatted provider data for forms
 */
export function formatProviderDataForForm(providerData) {
  if (!providerData) return null;

  // Extract provider data from the response (which might have a provider key or be direct)
  const provider = providerData.provider || providerData;

  // Format additional_locations from strings to objects
  const additionalLocations = Array.isArray(provider.additional_locations)
    ? provider.additional_locations.map(loc => {
      // Handle different data types safely
      if (typeof loc === 'string') {
        // Try to parse location string like "123 Main St, City, State 12345"
        const parts = loc.split(',').map(part => part.trim());
        const lastPart = parts.length > 2 ? parts[2] : '';

        // Extract postal code (could be at the end of the last part)
        const postalCodeMatch = lastPart.match(/\d{5}(-\d{4})?$/);
        const postalCode = postalCodeMatch ? postalCodeMatch[0] : '';
        const state = postalCodeMatch ? lastPart.replace(postalCodeMatch[0], '').trim() : lastPart;

        return {
          address: parts[0] || '',
          city: parts.length > 1 ? parts[1] : '',
          state: state || '',
          postal_code: postalCode || ''
        };
      } else if (typeof loc === 'object' && loc !== null) {
        // If it's already an object, use it as-is but ensure postal_code is a string
        return {
          address: loc.address || '',
          city: loc.city || '',
          state: loc.state || '',
          postal_code: loc.postal_code ? String(loc.postal_code) : ''
        };
      } else {
        // Fallback for other data types
        return {
          address: '',
          city: '',
          state: '',
          postal_code: ''
        };
      }
    })
    : [];

  // Format social_media from object to array of objects
  const socialMedia = provider.social_media && typeof provider.social_media === 'object'
    ? Object.entries(provider.social_media).map(([platform, url]) => ({
      platform,
      url
    }))
    : [];

  // Format office_hours from object to array of objects
  const officeHours = provider.office_hours && typeof provider.office_hours === 'object'
    ? Object.entries(provider.office_hours).flatMap(([day, slots]) =>
      Array.isArray(slots) ? slots.map(slot => ({
        day,
        open: slot.open || '',
        close: slot.close || ''
      })) : []
    )
    : [];

  return {
    // Basic info
    id: provider.id || '',
    provider_name: provider.provider_name || '',
    provider_type: provider.provider_type || '',
    status: provider.status || 'unverified',
    slug: provider.slug || '',

    // Address
    address_line1: provider.address_line1 || '',
    address_line2: provider.address_line2 || '',
    address_line3: provider.address_line3 || '',
    city: provider.city || '',
    state: provider.state || '',
    postal_code: provider.postal_code || '',
    latitude: provider.latitude || null,
    longitude: provider.longitude || null,

    // Contact and specializations
    phone_numbers: Array.isArray(provider.phone_numbers) ? provider.phone_numbers : [],
    website_url: provider.website_url || '',
    social_media: socialMedia,
    specializations: Array.isArray(provider.specializations) ? provider.specializations : [],
    services: Array.isArray(provider.services) ? provider.services : [],
    client_types: Array.isArray(provider.client_types) ? provider.client_types : [],

    // Professional info
    education: Array.isArray(provider.education) ? provider.education : [],
    certifications: Array.isArray(provider.certifications) ? provider.certifications : [],
    years_of_experience: provider.years_of_experience || null,
    spoken_languages: Array.isArray(provider.spoken_languages) ? provider.spoken_languages : [],

    // Business operations
    additional_locations: additionalLocations,
    average_response_time_minutes: provider.average_response_time_minutes || null,
    accepts_new_clients: provider.accepts_new_clients === true,
    virtual_consultations: provider.virtual_consultations === true,
    office_hours: officeHours,
    parking_info: provider.parking_info || '',
    payment_methods: Array.isArray(provider.payment_methods) ? provider.payment_methods : [],
    accessibility_options: Array.isArray(provider.accessibility_options) ? provider.accessibility_options : [],

    // Legal-specific fields
    provider_staff: Array.isArray(provider.provider_staff) ? provider.provider_staff : [],
    affiliated_organizations: Array.isArray(provider.affiliated_organizations) ? provider.affiliated_organizations : [],
    services_offered: Array.isArray(provider.services_offered) ? provider.services_offered : [],
    technology_available: Array.isArray(provider.technology_available) ? provider.technology_available : [],
    awards_accreditations: Array.isArray(provider.awards_accreditations) ? provider.awards_accreditations : [],
    court_representations: Array.isArray(provider.court_representations) ? provider.court_representations : [],
    pro_bono_services: Array.isArray(provider.pro_bono_services) ? provider.pro_bono_services : [],
    case_types_handled: Array.isArray(provider.case_types_handled) ? provider.case_types_handled : [],

    // Additional info
    additional_info: provider.additional_info || '',
    tags: Array.isArray(provider.tags) ? provider.tags : [],
    views: provider.views || 0,

    // Profile image
    profile_image: provider.profile_image || '',

    // Status
    is_draft: provider.status === 'draft'
  };
}

/**
 * Format form data to API-friendly format
 * @param {Object} formData - Provider form data
 * @returns {Object} - API-friendly provider data
 */
export function formatFormDataForApi(formData) {
  // Transform additional_locations from array of objects to array of objects
  const additionalLocations = Array.isArray(formData.additional_locations)
    ? formData.additional_locations
        .filter(loc => loc.address || loc.city || loc.state || loc.postal_code) // Filter out blank entries
        .map(loc => ({
          address: loc.address || '',
          city: loc.city || '',
          state: loc.state || '',
          postal_code: loc.postal_code ? String(loc.postal_code) : null
        }))
    : [];

  // Transform social_media from array of objects to key-value map
  const socialMedia = Array.isArray(formData.social_media)
    ? formData.social_media
        .filter(item => item.platform && item.url) // Only include if both platform and url exist
        .reduce((obj, item) => {
          obj[item.platform] = item.url;
          return obj;
        }, {})
    : {};

  // Transform office_hours from array of objects to map with days as keys
  const officeHours = Array.isArray(formData.office_hours)
    ? formData.office_hours
        .filter(item => item.day && (item.open || item.close)) // Filter out entries without day or times
        .reduce((obj, item) => {
          if (!obj[item.day]) {
            obj[item.day] = [];
          }
          obj[item.day].push({
            open: item.open || '',
            close: item.close || ''
          });
          return obj;
        }, {})
    : {};

  // Helper function to filter out blank strings from arrays
  const filterBlankStrings = (arr) => arr.filter(item => item && item.trim() !== '');

  // Helper function to filter out blank objects (objects with no meaningful values)
  const filterBlankObjects = (arr) => arr.filter(item => {
    if (typeof item === 'string') return item && item.trim() !== '';
    if (typeof item === 'object' && item !== null) {
      return Object.values(item).some(val => val !== null && val !== undefined && val !== '');
    }
    return false;
  });

  return {
    // Include ID if we're updating
    ...(formData.id ? { id: formData.id } : {}),

    // Basic info
    provider_name: formData.provider_name || '',
    provider_type: formData.provider_type || '',
    status: formData.status || 'unverified',
    is_draft: formData.is_draft === true,

    // Address
    address_line1: formData.address_line1 || '',
    address_line2: formData.address_line2 || '',
    address_line3: formData.address_line3 || '',
    city: formData.city || '',
    state: formData.state || '',
    postal_code: formData.postal_code ? parseInt(formData.postal_code, 10) : null,

    // Contact and specializations
    phone_numbers: Array.isArray(formData.phone_numbers) ? filterBlankStrings(formData.phone_numbers) : [],
    website_url: formData.website_url || '',
    social_media: socialMedia,
    specializations: Array.isArray(formData.specializations) ? filterBlankStrings(formData.specializations) : [],
    services: Array.isArray(formData.services) ? filterBlankStrings(formData.services) : [],
    client_types: Array.isArray(formData.client_types) ? filterBlankStrings(formData.client_types) : [],

    // Professional info
    education: Array.isArray(formData.education) ? filterBlankObjects(formData.education) : [],
    certifications: Array.isArray(formData.certifications) ? filterBlankObjects(formData.certifications) : [],
    years_of_experience: formData.years_of_experience ? parseInt(formData.years_of_experience, 10) : null,
    spoken_languages: Array.isArray(formData.spoken_languages) ? filterBlankStrings(formData.spoken_languages) : [],

    // Business operations
    additional_locations: additionalLocations,
    average_response_time_minutes: formData.average_response_time_minutes ? parseInt(formData.average_response_time_minutes, 10) : null,
    accepts_new_clients: formData.accepts_new_clients === true,
    virtual_consultations: formData.virtual_consultations === true,
    office_hours: officeHours,
    parking_info: formData.parking_info || '',
    payment_methods: Array.isArray(formData.payment_methods) ? filterBlankStrings(formData.payment_methods) : [],
    accessibility_options: Array.isArray(formData.accessibility_options) ? filterBlankStrings(formData.accessibility_options) : [],

    // Legal-specific fields
    provider_staff: Array.isArray(formData.provider_staff) ? filterBlankObjects(formData.provider_staff) : [],
    affiliated_organizations: Array.isArray(formData.affiliated_organizations) ? filterBlankStrings(formData.affiliated_organizations) : [],
    services_offered: Array.isArray(formData.services_offered) ? filterBlankStrings(formData.services_offered) : [],
    technology_available: Array.isArray(formData.technology_available) ? filterBlankStrings(formData.technology_available) : [],
    awards_accreditations: Array.isArray(formData.awards_accreditations) ? filterBlankStrings(formData.awards_accreditations) : [],
    court_representations: Array.isArray(formData.court_representations) ? filterBlankStrings(formData.court_representations) : [],
    pro_bono_services: Array.isArray(formData.pro_bono_services) ? filterBlankStrings(formData.pro_bono_services) : [],
    case_types_handled: Array.isArray(formData.case_types_handled) ? filterBlankStrings(formData.case_types_handled) : [],

    // Additional info
    additional_info: formData.additional_info || '',
    tags: Array.isArray(formData.tags) ? filterBlankStrings(formData.tags) : [],

    // Profile image
    profile_image: formData.profile_image || ''
  };
}

/**
 * Save a provider (create or update)
 * @param {string} token - JWT token
 * @param {Object} provider - Provider data
 * @returns {Promise<Object>} - Saved provider
 */
export async function saveProvider(token, providerData) {
  if (!token) {
    throw new Error('Authentication required');
  }

  // Transform form data to API format
  const apiData = formatFormDataForApi(providerData);

  const method = providerData.id ? 'PUT' : 'POST';
  const url = providerData.id
    ? `${API_BASE_URL}/providers/protected/${providerData.id}`
    : `${API_BASE_URL}/providers/protected`;

  const requestBody = JSON.stringify(apiData);

  const response = await fetch(url, {
    method,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'X-API-Key': API_KEY
    },
    body: requestBody
  });

  // Better error handling
  if (!response.ok) {
    const errorText = await response.text();
    console.error('=== SERVICE: Error response ===');
    console.error('Error text:', errorText);

    let errorMessage = `HTTP error! status: ${response.status}`;

    try {
      const errorData = JSON.parse(errorText);
      if (errorData.error) {
        errorMessage = errorData.error;
      }
    } catch (e) {
      // If can't parse as JSON, use the raw text
      if (errorText) {
        errorMessage = errorText;
      }
    }

    throw new Error(errorMessage);
  }

  const result = await response.json();

  return result;
}

/**
 * Claim a provider profile
 * @param {string} token - JWT authentication token
 * @param {Object|FormData} claimData - Provider claim data or FormData for file upload
 * @param {boolean} isFormData - Whether the data is FormData (for file uploads)
 * @returns {Promise<Object>} - Claim submission result
 */
export async function claimProviderProfile(token, claimData, isFormData = false) {
  const headers = {
    'Authorization': `Bearer ${token}`,
    'X-API-Key': API_KEY
  };

  // Don't set Content-Type for FormData (browser will set it with boundary)
  if (!isFormData) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(`${API_BASE_URL}/providers/claim`, {
    method: 'POST',
    headers: headers,
    body: isFormData ? claimData : JSON.stringify(claimData)
  });

  if (!response.ok) {
    if (response.status === 400) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Invalid claim data');
    } else if (response.status === 409) {
      throw new Error('This provider profile has already been claimed');
    }
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
}

/**
 * Process and validate image file to ensure compatibility
 * @param {File} file - The image file to process
 * @param {Object} options - Processing options
 * @returns {Promise<File>} - Processed image file
 */
export function processImageForUpload(file, options = {}) {
  return new Promise((resolve, reject) => {
    // Default options
    const config = {
      maxWidth: 1920,
      maxHeight: 1920,
      quality: 0.85,
      format: 'image/jpeg', // Force JPEG to avoid transparency issues
      fillColor: '#FFFFFF', // White background for transparency
      ...options
    };

    // Validate file type
    if (!file.type.startsWith('image/')) {
      reject(new Error('Please select a valid image file.'));
      return;
    }

    // Check file size (e.g., max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      reject(new Error('Image file is too large. Please choose a file smaller than 10MB.'));
      return;
    }

    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    img.onload = () => {
      try {
        // Calculate new dimensions while maintaining aspect ratio
        let { width, height } = img;

        if (width > config.maxWidth || height > config.maxHeight) {
          const ratio = Math.min(config.maxWidth / width, config.maxHeight / height);
          width = Math.floor(width * ratio);
          height = Math.floor(height * ratio);
        }

        // Set canvas dimensions
        canvas.width = width;
        canvas.height = height;

        // Fill canvas with solid background color (removes transparency)
        ctx.fillStyle = config.fillColor;
        ctx.fillRect(0, 0, width, height);

        // Enable high-quality image rendering
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        // Draw the image on the canvas
        ctx.drawImage(img, 0, 0, width, height);

        // Convert to blob
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Failed to process image. Please try a different image.'));
              return;
            }

            // Create new file with processed image
            const processedFile = new File(
              [blob],
              `processed_${file.name.replace(/\.[^/.]+$/, '.jpg')}`, // Change extension to .jpg
              {
                type: config.format,
                lastModified: Date.now()
              }
            );

            resolve(processedFile);
          },
          config.format,
          config.quality
        );

      } catch (error) {
        reject(new Error('Failed to process image. Please try a different image.'));
      }
    };

    img.onerror = () => {
      reject(new Error('Invalid image file. Please select a valid image.'));
    };

    // Load the image
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Create image preview URL
 * @param {File} file - Image file
 * @returns {string} - Object URL for preview
 */
export function createImagePreview(file) {
  return URL.createObjectURL(file);
}

/**
 * Cleanup image preview URL
 * @param {string} url - Object URL to cleanup
 */
export function cleanupImagePreview(url) {
  if (url && url.startsWith('blob:')) {
    URL.revokeObjectURL(url);
  }
}

/**
 * Upload a provider profile image with processing
 * @param {string} token - JWT authentication token
 * @param {File} imageFile - The image file to upload
 * @param {Object} processingOptions - Image processing options
 * @returns {Promise<Object>} - Response with image URL info or error details
 */
export async function uploadProviderProfileImage(token, imageFile, processingOptions = {}) {
  try {
    // Process the image first
    console.log('Processing image for upload...');
    const processedImage = await processImageForUpload(imageFile, processingOptions);
    console.log('Image processed successfully:', {
      originalSize: imageFile.size,
      processedSize: processedImage.size,
      originalType: imageFile.type,
      processedType: processedImage.type
    });

    // Create FormData with processed image
    const formData = new FormData();
    formData.append('file', processedImage);

    const apiUrl = `${API_BASE_URL}/providers/protected/profile-image`;
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'X-API-Key': API_KEY
      },
      body: formData
    });

    if (!response.ok) {
      let errorMessage = `Failed to upload image (${response.status})`;

      try {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } else {
          const textResponse = await response.text();
          if (textResponse) {
            errorMessage = textResponse;
          }
        }
      } catch (parseError) {
        console.error('Error parsing error response:', parseError);
      }

      return {
        success: false,
        error: errorMessage,
        serverIssue: true
      };
    }

    // Handle successful response
    try {
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const data = await response.json();
        return {
          success: true,
          image_url: data.presigned_url || data.profile_image,
          processedFile: processedImage // Return processed file info
        };
      } else {
        const textResponse = await response.text();
        return {
          success: true,
          message: 'Image uploaded successfully',
          text_response: textResponse,
          processedFile: processedImage
        };
      }
    } catch (parseError) {
      console.error('Error parsing success response:', parseError);
      return {
        success: false,
        error: 'Could not parse server response',
        serverIssue: true
      };
    }
  } catch (error) {
    console.error('Error in uploadProviderProfileImage:', error);
    return {
      success: false,
      error: error.message || 'Failed to process and upload image',
      serverIssue: false
    };
  }
}

/**
 * Get a provider's profile image URL
 * @param {string} providerId - The ID of the provider
 * @returns {Promise<Object>} - URL of the profile image or error details
 */
export async function getProviderProfileImage(providerId) {
  try {
    const apiUrl = `${API_BASE_URL}/providers/${providerId}/profile-image`;

    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': API_KEY
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const data = await response.json();

    return {
      success: true,
      image_url: data.presigned_url || data.profile_image || null
    };
  } catch (error) {
    console.error('Error in getProviderProfileImage:', error);
    return {
      success: false,
      error: error.message,
      serverIssue: true
    };
  }
}

/**
 * Get the current provider's profile image URL
 * @param {string} token - JWT authentication token
 * @param {string} providerId - The provider's ID
 * @returns {Promise<Object>} - URL of the profile image or error details
 */
export async function getCurrentProviderProfileImage(token, providerId) {
  if (!providerId) {
    return {
      success: false,
      error: "Provider ID is required",
      serverIssue: false
    };
  }

  try {
    // We can use the public endpoint since we already know our provider ID
    return await getProviderProfileImage(providerId);
  } catch (error) {
    console.error('Error in getCurrentProviderProfileImage:', error);
    return {
      success: false,
      error: error.message,
      serverIssue: true
    };
  }
}

/**
 * Upload a provider facility image with processing
 * @param {string} token - JWT authentication token
 * @param {string} providerId - The provider's ID
 * @param {File} imageFile - The image file to upload
 * @param {Object} metadata - Image metadata (type, description, display order)
 * @param {Object} processingOptions - Image processing options
 * @returns {Promise<Object>} - Response with image details or error
 */
export async function uploadProviderImage(token, providerId, imageFile, metadata = {}, processingOptions = {}) {
  if (!token || !providerId || !imageFile) {
    return {
      success: false,
      error: "Missing required parameters",
      serverIssue: false
    };
  }

  try {
    // Process the image first
    console.log('Processing provider image...');
    const processedImage = await processImageForUpload(imageFile, processingOptions);

    // Create FormData with processed image
    const formData = new FormData();
    formData.append('file', processedImage);

    // Build the query string for metadata
    const params = new URLSearchParams();
    if (metadata.image_type) params.append('image_type', metadata.image_type);
    if (metadata.description) params.append('description', metadata.description);
    if (metadata.display_order !== undefined) params.append('display_order', metadata.display_order);

    const apiUrl = `${API_BASE_URL}/providers/protected/${providerId}/images?${params.toString()}`;

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'X-API-Key': API_KEY
      },
      body: formData
    });

    if (!response.ok) {
      let errorMessage = `Failed to upload image (${response.status})`;

      try {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } else {
          const textResponse = await response.text();
          if (textResponse) {
            errorMessage = textResponse;
          }
        }
      } catch (parseError) {
        console.error('Error parsing error response:', parseError);
      }

      return {
        success: false,
        error: errorMessage,
        serverIssue: true
      };
    }

    const data = await response.json();
    return {
      success: true,
      image: data,
      processedFile: processedImage
    };
  } catch (error) {
    console.error('Error uploading provider image:', error);
    return {
      success: false,
      error: error.message || 'Failed to process and upload image',
      serverIssue: false
    };
  }
}

/**
 * Get all images for a provider
 * @param {string} providerId - The provider's ID
 * @param {string} typeFilter - Optional filter by image type
 * @returns {Promise<Object>} - Array of provider images or error
 */
export async function getProviderImages(providerId, typeFilter = null) {
  try {
    let apiUrl = `${API_BASE_URL}/providers/${providerId}/images`;

    if (typeFilter) {
      apiUrl += `?type_filter=${encodeURIComponent(typeFilter)}`;
    }

    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': API_KEY
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const data = await response.json();
    return {
      success: true,
      images: data
    };
  } catch (error) {
    console.error('Error fetching provider images:', error);
    return {
      success: false,
      error: error.message,
      serverIssue: true
    };
  }
}

/**
 * Get a specific provider image by ID
 * @param {string} providerId - The provider's ID
 * @param {string} imageId - The image ID
 * @returns {Promise<Object>} - Image details or error
 */
export async function getProviderImage(providerId, imageId) {
  try {
    const apiUrl = `${API_BASE_URL}/providers/${providerId}/images/${imageId}`;

    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': API_KEY
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const data = await response.json();
    return {
      success: true,
      image: data
    };
  } catch (error) {
    console.error('Error fetching provider image:', error);
    return {
      success: false,
      error: error.message,
      serverIssue: true
    };
  }
}

/**
 * Update a provider image's metadata
 * @param {string} token - JWT authentication token
 * @param {string} providerId - The provider's ID
 * @param {string} imageId - The image ID
 * @param {Object} metadata - Updated metadata (type, description, display order)
 * @returns {Promise<Object>} - Updated image details or error
 */
export async function updateProviderImage(token, providerId, imageId, metadata) {
  try {
    const apiUrl = `${API_BASE_URL}/providers/protected/${providerId}/images/${imageId}`;

    const response = await fetch(apiUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-API-Key': API_KEY
      },
      body: JSON.stringify(metadata)
    });

    if (!response.ok) {
      let errorMessage = `HTTP error! Status: ${response.status}`;
      const errorData = await response.json().catch(() => ({}));

      if (errorData.error) {
        errorMessage = errorData.error;
      }

      throw new Error(errorMessage);
    }

    const data = await response.json();
    return {
      success: true,
      image: data
    };
  } catch (error) {
    console.error('Error updating provider image:', error);
    return {
      success: false,
      error: error.message,
      serverIssue: true
    };
  }
}

/**
 * Delete a provider image
 * @param {string} token - JWT authentication token
 * @param {string} providerId - The provider's ID
 * @param {string} imageId - The image ID to delete
 * @returns {Promise<Object>} - Success status or error
 */
export async function deleteProviderImage(token, providerId, imageId) {
  try {
    const apiUrl = `${API_BASE_URL}/providers/protected/${providerId}/images/${imageId}`;

    const response = await fetch(apiUrl, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'X-API-Key': API_KEY
      }
    });

    if (!response.ok) {
      let errorMessage = `HTTP error! Status: ${response.status}`;

      try {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        }
      } catch (e) {
        // Ignore parse errors for non-JSON responses
      }

      throw new Error(errorMessage);
    }

    return {
      success: true
    };
  } catch (error) {
    console.error('Error deleting provider image:', error);
    return {
      success: false,
      error: error.message,
      serverIssue: true
    };
  }
}